import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrder } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet';
import styles from '../styles/PackagesPage.module.css';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { trackViewPackages, trackSelectPackage } from '../utils/pixelEvents';

const PackagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrder, updateOrderDetails, getPackagePrice } = useOrder();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('Premium');
  const [currency, setCurrency] = useState({ code: 'USD', symbol: '$', rate: 1 });
  const [expandedPackage, setExpandedPackage] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');

  const currencies = {
    USD: { code: 'USD', symbol: '$', rate: 1 },
    EUR: { code: 'EUR', symbol: '€', rate: 0.93 },
    GBP: { code: 'GBP', symbol: '£', rate: 0.82 },
    CAD: { code: 'CAD', symbol: 'C$', rate: 1.38 },
    AUD: { code: 'AUD', symbol: 'A$', rate: 1.52 },
  };

  // Base package data
  const basePackages = [
    {
      id: 'Basic',
      name: 'Basic',
      price: 29,
      features: [
        'One custom song (up to 2 minutes)',
        'Single music style to match your vision',
        'Basic personalization to capture your story',
        'No revisions included',
        'Limited personalization options',
      ],
      description: '🎵 Perfect for First-Time Creators',
      idealFor: [
        'Meaningful personal gifts',
        'Capturing a special memory',
        'Those wanting to try a unique musical experience',
      ],
      emoji: '🎵',
      popular: false,
      value: false,
    },
    {
      id: 'Premium',
      name: 'Premium',
      price: 49,
      features: [
        'One custom song (up to 3 minutes)',
        'Blend two complementary music styles',
        'Advanced personalization for a unique sound',
        'One revision to perfect your song',
        'Two musical variations to explore different approaches',
      ],
      description: '🌟 Comprehensive Musical Storytelling',
      idealFor: [
        'Those seeking more creative depth',
        'Expressing complex emotions or stories',
        'Creating a truly personalized musical experience',
      ],
      emoji: '🌟',
      popular: true,
      value: true,
    },
    {
      id: 'Deluxe',
      name: 'Deluxe',
      price: 99,
      features: [
        'One custom song (up to 4 minutes)',
        'Blend three distinct music styles',
        'Premium-level personalization',
        'Two full revisions',
        'Four musical variations for extensive exploration',
      ],
      description: '🏆 Premium, Unlimited Creativity',
      idealFor: [
        'Crafting a deeply personal musical narrative',
        'Those wanting maximum creative flexibility',
        'Creating a truly one-of-a-kind musical tribute',
      ],
      emoji: '🏆',
      popular: false,
      value: false,
    },
  ];

  // Memoized packages with discounted prices
  const packages = useMemo(() => {
    return basePackages.map((pkg) => ({
      ...pkg,
      discountedPrice: discountAmount
        ? Math.round((pkg.price * (1 - discountAmount / 100)) * 100) / 100
        : 0,
    }));
  }, [discountAmount]);

  const convertPrice = (usdPrice) => {
    return (usdPrice * currency.rate).toFixed(2);
  };

  const applyDiscount = async (code = '') => {
    const discountToApply = code || discountCode;
    if (!discountToApply) return;

    setDiscountLoading(true);
    setDiscountError('');

    try {
      if (!db) {
        throw new Error('Database connection not initialized');
      }

      const discountsRef = collection(db, 'discounts');
      const q = query(
        discountsRef,
        where('code', '==', discountToApply),
        where('isActive', '==', true),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setDiscountError('Invalid discount code');
        setDiscountAmount(0);
        setDiscountApplied(false);
        return;
      }

      const discountData = querySnapshot.docs[0].data();
      const discountValue = Number(discountData.value); // Ensure value is a number

      if (isNaN(discountValue) || discountValue <= 0) {
        setDiscountError('Invalid discount value');
        setDiscountAmount(0);
        setDiscountApplied(false);
        return;
      }

      if (discountData.expirationDate && new Date() > new Date(discountData.expirationDate.seconds * 1000)) {
        setDiscountError('This discount code has expired');
        setDiscountAmount(0);
        setDiscountApplied(false);
        return;
      }

      if (discountData.usageLimit && discountData.usageCount >= discountData.usageLimit) {
        setDiscountError('This discount code has reached its usage limit');
        setDiscountAmount(0);
        setDiscountApplied(false);
        return;
      }

      setDiscountAmount(discountValue);
      setDiscountApplied(true);
      setDiscountCode(discountToApply);
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('Error applying discount code');
      setDiscountAmount(0);
      setDiscountApplied(false);
    } finally {
      setDiscountLoading(false);
    }
  };

  useEffect(() => {
    trackViewPackages();

    const searchParams = new URLSearchParams(location.search);
    const urlDiscount = searchParams.get('discount');
    const storedDiscount = localStorage.getItem('activeDiscount');

    if (urlDiscount) {
      setDiscountCode(urlDiscount);
      applyDiscount(urlDiscount);
    } else if (storedDiscount) {
      setDiscountCode(storedDiscount);
      applyDiscount(storedDiscount);
    }
  }, [location.search]);

  const handlePackageSelect = (pkgName) => {
    setSelectedPackage(pkgName);
    const price = getPackagePrice(pkgName);
    trackSelectPackage(pkgName, price);

    updateOrderDetails('package', pkgName);
    updateOrderDetails('genres', []);
    if (discountApplied) {
      updateOrderDetails('discount', {
        code: discountCode,
        amount: discountAmount,
        applied: true,
      });
    }

    navigate('/genres');
  };

  const handleTabClick = (pkgName) => {
    setActiveTab(pkgName);
  };

  const handleAccordionToggle = (pkgName) => {
    setExpandedPackage(expandedPackage === pkgName ? null : pkgName);
  };

  const highlightFeature = (feature) => {
    const words = feature.split(' ');
    return words.map((word, i) => {
      if (/\d/.test(word) || ['Premium', 'Advanced'].includes(word)) {
        return (
          <span key={i} className={styles.featureHighlight}>
            {word}{' '}
          </span>
        );
      }
      return word + ' ';
    });
  };

  const comparisonFeatures = useMemo(
    () => [
      { label: 'Song Duration', basic: 'Up to 2 minutes', premium: 'Up to 3 minutes', deluxe: 'Up to 4 minutes' },
      { label: 'Music Styles', basic: '1 style', premium: '2 styles', deluxe: '3 styles' },
      { label: 'Personalization Level', basic: 'Basic', premium: 'Advanced', deluxe: 'Premium' },
      { label: 'Revisions', basic: 'None', premium: '1 revision', deluxe: '2 revisions' },
      { label: 'Musical Variations', basic: 'None', premium: '2 variations', deluxe: '4 variations' },
      {
        label: 'Price',
        basic: discountApplied
          ? `${currency.symbol}${convertPrice(packages[0].discountedPrice)}`
          : `${currency.symbol}${convertPrice(29)}`,
        premium: discountApplied
          ? `${currency.symbol}${convertPrice(packages[1].discountedPrice)}`
          : `${currency.symbol}${convertPrice(49)}`,
        deluxe: discountApplied
          ? `${currency.symbol}${convertPrice(packages[2].discountedPrice)}`
          : `${currency.symbol}${convertPrice(99)}`,
      },
    ],
    [currency, discountApplied, packages, convertPrice]
  );

  return (
    <div className={styles.packagesContainer}>
      <Helmet>
        <title>Choose Your Song Package | Custom Music Creation | MakeMeSongs</title>
        <meta
          name="description"
          content="Select from Basic ($29), Premium ($49), or Deluxe ($99) custom song packages. Each offers different features to make your personalized song perfect for any occasion."
        />
        <meta
          name="keywords"
          content="custom song packages, personalized music pricing, music gift packages, song writing service, premium music creation"
        />
        <meta property="og:title" content="Choose Your Song Package | MakeMeSongs" />
        <meta
          property="og:description"
          content="Create the perfect custom song with our Basic, Premium, or Deluxe packages. Professionally crafted music tailored to your special occasion."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://makemesongs.com/packages" />
        <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
        <link rel="canonical" href="https://makemesongs.com/packages" />
      </Helmet>
      <div className={styles.header}>
        <h1>Choose Your Song Package</h1>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', margin: '15px 0' }}>
          <select
            value={currency.code}
            onChange={(e) => setCurrency(currencies[e.target.value])}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            {Object.keys(currencies).map((code) => (
              <option key={code} value={code}>
                {code} ({currencies[code].symbol})
              </option>
            ))}
          </select>
          <span style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'italic' }}>
            *All payments are processed in USD. Other currencies shown for reference only.
          </span>
        </div>
        <p>
          Select the package that best fits your needs. Each package offers different features to make your custom song
          perfect for your special occasion.
          <span className={styles.mobileInstruction}> Tap a tab to explore packages!</span>
        </p>
      </div>

      {discountApplied && (
        <div className={styles.discountBanner}>
          <div className={styles.discountContent}>
            <div className={styles.discountIcon}>🎁</div>
            <div className={styles.discountInfo}>
              <span className={styles.discountTitle}>
                {discountCode === 'MOM2025' ? "Mother's Day Special" : 'Discount Applied'}:
              </span>
              <span className={styles.discountValue}>{discountAmount}% OFF</span>
              <div className={styles.discountCode}>Code: {discountCode}</div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tabNav}>
        {packages.map((pkg) => (
          <button
            key={pkg.name}
            className={`${styles.tabButton} ${activeTab === pkg.name ? styles.activeTab : ''}`}
            onClick={() => handleTabClick(pkg.name)}
          >
            <span className={styles.tabLabel}>{pkg.name}</span>
            {pkg.popular && <span className={styles.tabBadge}>🎵 Top Pick</span>}
          </button>
        ))}
      </div>

      <div className={styles.packagesGrid}>
        {packages.map((pkg) => (
          <div
            key={pkg.name}
            className={`${styles.packageCard} ${selectedPackage === pkg.name ? styles.selected : ''} ${
              activeTab === pkg.name ? styles.activeCard : styles.inactiveCard
            }`}
            onClick={() => setSelectedPackage(pkg.name)}
          >
            {pkg.popular && <div className={styles.popularBadge}>Most Popular</div>}
            {pkg.value && <div className={styles.valueRibbon}>Best Value</div>}
            {selectedPackage === pkg.name && <div className={styles.selectedOverlay}>Selected</div>}
            <h2>{pkg.name}</h2>
            <p className={styles.packageDescription}>{pkg.description}</p>
            <div className={styles.priceContainer}>
              {discountApplied && pkg.discountedPrice ? (
                <>
                  <span className={styles.originalPrice}>
                    {currency.symbol}
                    {convertPrice(pkg.price)}
                  </span>
                  <div className={styles.price}>
                    {currency.symbol}
                    {convertPrice(pkg.discountedPrice)}
                  </div>
                  <div className={styles.discountTag}>{discountAmount}% OFF</div>
                </>
              ) : (
                <div className={styles.price}>
                  {currency.symbol}
                  {convertPrice(pkg.price)}
                </div>
              )}
              {currency.code !== 'USD' && (
                <div style={{ fontSize: '0.7em', color: '#666', marginTop: '5px' }}>
                  (${discountApplied && pkg.discountedPrice ? pkg.discountedPrice : pkg.price} USD)
                </div>
              )}
            </div>
            <ul>
              {pkg.features.map((feature, index) => (
                <li key={index}>{highlightFeature(feature)}</li>
              ))}
            </ul>
            <div className={styles.idealForSection}>
              <strong>Ideal For:</strong>
              <ul>
                {pkg.idealFor.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <button className={styles.selectButton} onClick={() => handlePackageSelect(pkg.name)}>
              {selectedPackage === pkg.name ? 'Selected' : 'Select Package'}
            </button>
          </div>
        ))}
      </div>

      <div className={styles.discountSection}>
        <div className={styles.discountInputContainer}>
          <input
            type="text"
            className={styles.discountInput}
            placeholder="Have a discount code?"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
            disabled={discountApplied || discountLoading}
          />
          <button
            className={styles.discountButton}
            onClick={() => applyDiscount()}
            disabled={discountApplied || discountLoading || !discountCode}
          >
            {discountLoading ? 'Applying...' : 'Apply'}
          </button>
        </div>
        {discountError && <div className={styles.discountError}>{discountError}</div>}
      </div>

      <div className={styles.comparisonSection}>
        <h2>Compare Your Musical Journey</h2>
        <div className={styles.orangeDivider}></div>
        <p className={styles.comparisonIntro}>Find the perfect package for your story with a quick comparison of features.</p>
        <div className={styles.comparisonTable}>
          <div className={styles.tableHeader}>
            <div className={styles.headerCell}></div>
            {packages.map((pkg) => (
              <div
                key={pkg.name}
                className={`${styles.headerCell} ${pkg.popular ? styles.popularHeader : ''} ${
                  selectedPackage === pkg.name ? styles.selectedHeader : ''
                }`}
              >
                <span className={styles.packageEmoji}>{pkg.emoji}</span>
                {pkg.name}
              </div>
            ))}
          </div>
          {comparisonFeatures.map((feature, index) => (
            <div key={index} className={styles.tableRow}>
              <div className={styles.featureCell}>{feature.label}</div>
              <div className={styles.valueCell}>{feature.basic}</div>
              <div className={styles.valueCell}>{feature.premium}</div>
              <div className={styles.valueCell}>{feature.deluxe}</div>
            </div>
          ))}
          <div className={styles.tableRow}>
            <div className={styles.featureCell}></div>
            {packages.map((pkg) => (
              <div key={pkg.name} className={styles.valueCell}>
                <button
                  className={`${styles.selectButton} ${selectedPackage === pkg.name ? styles.selectedButton : ''}`}
                  onClick={() => handlePackageSelect(pkg.name)}
                >
                  {selectedPackage === pkg.name ? 'Selected' : 'Select Package'}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.comparisonAccordion}>
          {packages.map((pkg) => (
            <div key={pkg.name} className={styles.accordionItem}>
              <div
                className={`${styles.accordionHeader} ${selectedPackage === pkg.name ? styles.selectedHeader : ''} ${
                  pkg.popular ? styles.popularHeader : ''
                }`}
                onClick={() => handleAccordionToggle(pkg.name)}
              >
                <span className={styles.packageEmoji}>{pkg.emoji}</span>
                {pkg.name}
                <span className={styles.accordionIcon}>{expandedPackage === pkg.name ? '−' : '+'}</span>
              </div>
              <div className={`${styles.accordionContent} ${expandedPackage === pkg.name ? styles.expanded : ''}`}>
                <ul>
                  {comparisonFeatures.map((feature, index) => (
                    <li key={index}>
                      <span className={styles.featureLabel}>{feature.label}:</span>{' '}
                      {feature[pkg.name.toLowerCase()]}
                    </li>
                  ))}
                </ul>
                <button
                  className={`${styles.selectButton} ${selectedPackage === pkg.name ? styles.selectedButton : ''}`}
                  onClick={() => handlePackageSelect(pkg.name)}
                >
                  {selectedPackage === pkg.name ? 'Selected' : 'Select Package'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PackagesPage;